import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowUpRightIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  PaperAirplaneIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { COUNTRY_DATA, CountryData } from '../data/countryData';
import { VISUAL } from './layout';
import { PORTALS } from './portals';

interface ReplyLink {
  label: string;
  to?: string;
  href?: string;
}

interface Reply {
  text: string;
  links?: ReplyLink[];
  chips?: string[];
}

interface Message extends Reply {
  id: number;
  from: 'bot' | 'user';
}

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];
const DEFAULT_CHIPS = ['Get a quote', 'Do you ship to Japan?', 'What is IOR / EOR?', 'Talk to the team'];
const CONTACT_LINKS: ReplyLink[] = [
  { label: 'Request a quote', to: '/contact-us' },
  { label: 'Email sales', href: 'mailto:sales@truelog.com.sg' },
];

const GREETING: Reply = {
  text: "Hi, I'm the TrueLog assistant. Ask me about a destination country, Importer of Record, freight, warehousing or getting a quote.",
  chips: DEFAULT_CHIPS,
};

// Common shorthands mapped onto the names used in the country guides.
const ALIASES: Record<string, string> = {
  usa: 'united states',
  america: 'united states',
  uk: 'united kingdom',
  britain: 'united kingdom',
  england: 'united kingdom',
  uae: 'united arab emirates',
  emirates: 'united arab emirates',
  korea: 'south korea',
  holland: 'netherlands',
};

function findCountry(query: string): CountryData | undefined {
  let s = ` ${query.toLowerCase().replace(/[^a-z\s]/g, ' ')} `;
  Object.keys(ALIASES).forEach((alias) => {
    s = s.replace(new RegExp(`\\s${alias}\\s`, 'g'), ` ${ALIASES[alias]} `);
  });
  let best: CountryData | undefined;
  let bestLen = 0;
  Object.values(COUNTRY_DATA).forEach((country) => {
    [country.name.toLowerCase(), country.slug.replace(/-/g, ' ')].forEach((name) => {
      if (name.length > bestLen && s.includes(` ${name} `)) {
        best = country;
        bestLen = name.length;
      }
    });
  });
  return best;
}

// Scripted, deterministic answers built from the site's own content.
function replyFor(raw: string): Reply {
  const s = raw.trim().toLowerCase();

  const country = findCountry(s);
  if (country) {
    const m = country.metrics;
    return {
      text: `${country.name}: ${country.tagline}. Typical lead time is ${m.leadTime}, duties ${m.dutyRate}, tax ${m.taxRate}.${
        country.hasOffice ? ' We run our own office there.' : ' We deliver there through our vetted partner network.'
      }`,
      links: [
        { label: `${country.name} country guide`, to: `/countries/${country.slug}` },
        { label: 'Request a quote', to: '/contact-us' },
      ],
      chips: ['Get a quote', 'What is IOR / EOR?'],
    };
  }

  if (/\b(ship|deliver|send|export|import)\w*\s+(to|into)\b/.test(s) || /coverage|which countries|where do you|how many countries/.test(s)) {
    const offices = Object.values(COUNTRY_DATA)
      .filter((c) => c.hasOffice)
      .map((c) => c.name);
    return {
      text: `We serve 140+ countries as Importer and Exporter of Record, with our own offices in ${offices.join(
        ', ',
      )} and vetted partners everywhere else. Name a country and I will pull up its guide.`,
      links: [{ label: 'Global coverage map', to: '/global-coverage' }],
      chips: ['Do you ship to Japan?', 'Do you ship to Brazil?', 'Get a quote'],
    };
  }

  if (/quote|pric|cost|rate|rfq|how much|estimate|budget/.test(s)) {
    return {
      text: 'Quotes come from our team, usually within one business day. Share the origin, destination, what you are shipping (laptops, servers, network gear) and the quantity, and we will scope duties, taxes and transit for you.',
      links: CONTACT_LINKS,
      chips: ['What is IOR / EOR?', 'Talk to the team'],
    };
  }

  if (/\bior\b|\beor\b|importer|exporter|of record|customs|dut(y|ies)|\btax|complian|permit|licen|clearance/.test(s)) {
    return {
      text: 'TrueLog acts as your Importer or Exporter of Record: we hold the import licences, handle customs clearance and settle duties and taxes in the destination country, so you can deliver ICT hardware to markets where you have no legal entity.',
      links: [
        { label: 'Trade Compliance', to: '/services/cross-border-compliance' },
        { label: 'Request a quote', to: '/contact-us' },
      ],
      chips: ['Get a quote', 'Do you ship to India?'],
    };
  }

  if (/freight|\bair\b|ocean|\bsea\b|courier|transport|shipping|shipment|express/.test(s)) {
    return {
      text: 'We move ICT hardware by air, ocean and road, door to door, with packaging and insurance options suited to servers, laptops and network equipment.',
      links: [
        { label: 'Global Transport & Freight', to: '/services/global-transport-freight' },
        { label: 'Request a quote', to: '/contact-us' },
      ],
    };
  }

  if (/warehous|storage|stock|fulfil|inventory|staging|kitting|\bpick|\bpack|configur|imaging|asset tag/.test(s)) {
    return {
      text: 'Our bonded flagship warehouses in Singapore and Jebel Ali hold buffer stock right next to the airport, clear cargo in minutes and get it onto the next flight out.',
      links: [
        { label: 'Warehousing & Fulfilment', to: '/services/warehousing-fulfilment' },
        { label: 'Request a quote', to: '/contact-us' },
      ],
    };
  }

  if (/\brma\b|return|reverse|repair|recycl|itad|dispos|refurb|collect/.test(s)) {
    return {
      text: 'Reverse logistics is covered too: RMA collection, returns consolidation, repair routing and end-of-life handling for devices anywhere in our network.',
      links: [
        { label: 'Warehousing & Fulfilment', to: '/services/warehousing-fulfilment' },
        { label: 'Request a quote', to: '/contact-us' },
      ],
    };
  }

  if (/track|where is|status|tick-|\beta\b|delivery date/.test(s)) {
    return {
      text: `Live tracking, documents and check-ins are in your client portal: the ${PORTALS[0].label} for assets and tickets, the ${PORTALS[1].label} for freight.`,
      links: [
        ...PORTALS.map((portal) => ({ label: `Open the ${portal.label}`, href: portal.href })),
        { label: 'Contact support', to: '/contact-us' },
      ],
    };
  }

  if (/human|person|someone|team|sales|talk|speak|call|phone|email|contact|agent|representative/.test(s)) {
    return {
      text: 'Our team is in Singapore (UTC+8). Call +65 6909 3756, email sales@truelog.com.sg, or leave your details and we will get back to you within one business day.',
      links: [
        { label: 'Contact us', to: '/contact-us' },
        { label: 'Email sales', href: 'mailto:sales@truelog.com.sg' },
        { label: 'Call +65 6909 3756', href: 'tel:+6569093756' },
      ],
    };
  }

  if (/^(hi|hello|hey|good (morning|afternoon|evening))\b/.test(s)) return GREETING;

  if (/thank/.test(s)) {
    return { text: 'You are welcome. Anything else I can look up?', chips: DEFAULT_CHIPS };
  }

  if (/about|who are you|company|truelog|history|experience/.test(s)) {
    return {
      text: 'TrueLog is a Singapore-based ICT logistics specialist with 18+ years of experience, delivering hardware for SaaS platforms, hyperscalers, OEMs and global enterprises across 140+ countries.',
      links: [
        { label: 'About TrueLog', to: '/about-us' },
        { label: 'Why TrueLog', to: '/why-truelog' },
      ],
    };
  }

  return {
    text: 'I can help with destination countries, Importer / Exporter of Record, freight, warehousing and quotes. Try a country name, or pick one of these:',
    chips: DEFAULT_CHIPS,
    links: [{ label: 'Talk to our team', to: '/contact-us' }],
  };
}

const ReplyLinks: React.FC<{ links: ReplyLink[] }> = ({ links }) => (
  <div className="mt-2 flex flex-wrap gap-1.5">
    {links.map((link) =>
      link.to ? (
        <Link key={link.label} to={link.to} className="lp-chip !py-1 !text-[12px]">
          {link.label}
          <ArrowUpRightIcon className="h-3 w-3" />
        </Link>
      ) : (
        <a
          key={link.label}
          href={link.href}
          target={link.href && link.href.startsWith('http') ? '_blank' : undefined}
          rel="noopener noreferrer"
          className="lp-chip !py-1 !text-[12px]"
        >
          {link.label}
          <ArrowUpRightIcon className="h-3 w-3" />
        </a>
      ),
    )}
  </div>
);

const Assistant: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [bubbles, setBubbles] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const nextId = useRef(1);
  const listRef = useRef<HTMLDivElement>(null);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    if (VISUAL === 'warehouse') return undefined;
    const t = window.setTimeout(() => setBubbles(true), 1600);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(
    () => () => {
      timers.current.forEach((t) => window.clearTimeout(t));
    },
    [],
  );

  const push = (from: Message['from'], reply: Reply) => {
    const id = nextId.current;
    nextId.current += 1;
    setMessages((prev) => [...prev, { id, from, ...reply }]);
  };

  useEffect(() => {
    if (open && messages.length === 0) push('bot', GREETING);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, typing]);

  const send = (text: string) => {
    const q = text.trim();
    if (!q || typing) return;
    push('user', { text: q });
    setInput('');
    setTyping(true);
    const reply = replyFor(q);
    const t = window.setTimeout(() => {
      setTyping(false);
      push('bot', reply);
    }, 450 + Math.min(900, q.length * 20));
    timers.current.push(t);
  };

  return (
    <div className="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-3 sm:bottom-6 sm:right-6">
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 18, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.96 }}
            transition={{ duration: 0.28, ease: EASE }}
            className="w-[min(92vw,380px)] overflow-hidden rounded-[28px] border border-white/[0.12] bg-[#070b16]/85 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.9)] backdrop-blur-2xl"
          >
            <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
              <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#0E9ED5] to-[#385CF2] text-white">
                <ChatBubbleOvalLeftEllipsisIcon className="h-5 w-5" />
                <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-[#070b16] bg-emerald-400" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white">TrueLog assistant</p>
                <p className="text-[11px] text-white/50">Guided answers from our country guides</p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-full p-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
                aria-label="Close assistant"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            <div ref={listRef} className="lp-scrollbar max-h-[46vh] space-y-3 overflow-y-auto px-4 py-4">
              {messages.map((m, idx) => (
                <div key={m.id} className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[88%] px-3.5 py-2.5 text-[13.5px] leading-6 text-white ${
                      m.from === 'user' ? 'lp-msg-user' : 'lp-msg-bot'
                    }`}
                  >
                    <p>{m.text}</p>
                    {m.links && <ReplyLinks links={m.links} />}
                    {m.chips && idx === messages.length - 1 && !typing && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {m.chips.map((chip) => (
                          <button key={chip} type="button" onClick={() => send(chip)} className="lp-chip !py-1 !text-[12px]">
                            {chip}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {typing && (
                <div className="flex justify-start">
                  <div className="lp-msg-bot lp-typing px-3.5 py-3" aria-label="Assistant is typing">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send(input);
              }}
              className="flex items-center gap-2 border-t border-white/10 px-3 py-3"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about a country, IOR, freight..."
                aria-label="Message"
                className="min-w-0 flex-1 rounded-full border border-white/10 bg-white/[0.05] px-4 py-2.5 text-sm text-white placeholder-white/35 outline-none transition-colors focus:border-[#0E9ED5]/60"
              />
              <button
                type="submit"
                disabled={!input.trim()}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-slate-900 transition-opacity disabled:opacity-40"
                aria-label="Send"
              >
                <PaperAirplaneIcon className="h-4 w-4 -rotate-45" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!open && bubbles && !dismissed && (
          <motion.div
            key="bubbles"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.3 }}
            className="hidden flex-col items-end gap-2 sm:flex"
          >
            <motion.div
              initial={{ opacity: 0, y: 10, x: 10 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="lp-bubble relative pr-9"
            >
              Hello, I&apos;m the TrueLog assistant
              <button
                type="button"
                onClick={() => setDismissed(true)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-white/50 transition-colors hover:text-white"
                aria-label="Dismiss"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </motion.div>
            <motion.button
              type="button"
              initial={{ opacity: 0, y: 10, x: 10 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              transition={{ duration: 0.5, delay: 0.35, ease: EASE }}
              onClick={() => setOpen(true)}
              className="lp-bubble transition-colors hover:bg-white/10"
            >
              How can I help you?
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.1, duration: 0.6, ease: EASE }}
        whileTap={{ scale: 0.94 }}
        onClick={() => setOpen((o) => !o)}
        className="lp-assistant-btn"
        aria-label={open ? 'Close assistant' : 'Open assistant'}
        aria-expanded={open}
      >
        {open ? <XMarkIcon className="h-6 w-6" /> : <ChatBubbleOvalLeftEllipsisIcon className="h-6 w-6" />}
      </motion.button>
    </div>
  );
};

export default Assistant;
